<script setup lang="ts">
    import { ref, computed, defineProps, defineExpose, onBeforeMount } from "vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import { useStore } from "@/store";
    import { flashWarning } from "@/utils";
    import emitter from "tiny-emitter/instance";

    const props = withDefaults(
        defineProps<{
            uid: number;
            tabId: number;
            parameters: {
                register: any;
                limit: number;
            } | null;
        }>(),
        {
            parameters: () => ({ register: null, limit: 20 }),
        }
    );

    const { parameters, tabId } = props;
    const store = useStore();
    const register = parameters?.register ?? null;
    const studieKvalitetsregisterRef = ref();
    const formErrors = ref([]);
    const inputErrors = ref({});

    const hasUnsavedData = computed(() => studieKvalitetsregisterRef.value?.hasUnsavedData());
    const heading = computed(() =>
        register?.id ? "Ändra Studie/Kvalitetsregister" : "Skapa ny Studie/Kvalitetsregister"
    );

    const setTabName = () => {
        store.commit("tabs/setTabName", {
            id: tabId,
            name: register?.id ? "Redigera Studie/Kvalitetsregister" : "Skapa ny Studie/Kvalitetsregister",
        });
    };

    defineExpose({ hasUnsavedData, tabId });

    const initForm = async () => {
        return register ? { ...register } : {};
    };

    const submitForm = async (data: any) => {
        try {
            if (register?.id) {
                await store.dispatch("studieKvalitetsregister/updateStudieKvalitetsregister", {
                    studieKvalitetsregister: { ...data },
                    id: register?.id,
                    limit: parameters?.limit,
                });
            } else {
                await store.dispatch("studieKvalitetsregister/createStudieKvalitetsregister", {
                    studieKvalitetsregister: { ...data },
                    id: register?.id,
                    limit: parameters?.limit,
                });
            }
            store.dispatch("tabs/closeTab", tabId);
        } catch (e: any) {
            if (e.response) {
                formErrors.value = e.response.data["non_field_errors"];
                inputErrors.value = e.response.data;
            }
            flashWarning(
                register?.id ? "Kunde inte redigera kvalitetsregistret" : "Kunde inte spara kvalitetsregistret"
            );
        }
    };

    const cancelForm = async ({ hasUnsavedData }: { hasUnsavedData: boolean }) => {
        if (hasUnsavedData) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    { title: "Fortsätt redigera", type: "secondary" },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId);
                        },
                    },
                ],
            });
        } else {
            await store.dispatch("tabs/closeTab", tabId);
        }
    };

    onBeforeMount(async () => {
        setTabName();
    });
</script>

<template>
    <KForm
        ref="studieKvalitetsregisterRef"
        id="studie-kvalitetsregister"
        class="pad"
        :initial-values-getter="initForm"
        submit-content="Spara och stäng"
        cancel-content="Avbryt"
        @submit="submitForm"
        @cancel="cancelForm"
        :form-errors="formErrors"
        :errors="inputErrors"
    >
        <template #header>
            <admin-lock-border />
            <h2>{{ heading }}</h2>
        </template>
        <template #default>
            <div class="form-content">
                <div class="form-section">
                    <KText name="namn" label="Namn" validation="required" />
                    <KText name="ordning" label="Ordning" />
                </div>
                <FormulateErrors />
            </div>
        </template>
    </KForm>
</template>

<style lang="scss" scoped></style>
